import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";

// Layouts
const AdminLayout = lazy(() => import("@/layouts/AdminLayout"));
const PublicLayout = lazy(() => import("@/layouts/PublicLayout"));
const AuthLayout = lazy(() => import("@/layouts/AuthLayout"));

// Guards
const PrivateRoute = lazy(() => import("@/components/guards/PrivateRoute"));

// Pages — Public
const HomePage = lazy(() => import("@/pages/HomePage"));
const NgoPublicProfilePage = lazy(() => import("@/pages/NgoPublicProfilePage"));
const DonatePage = lazy(() => import("@/features/donations/DonatePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ProgramsPage = lazy(() => import("@/pages/ProgramsPage"));
const ProgramDetailPage = lazy(() => import("@/pages/ProgramDetailPage"));
const PublicEventsPage = lazy(() => import("@/pages/PublicEventsPage"));
const PublicGalleryPage = lazy(() => import("@/pages/PublicGalleryPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const VolunteerPage = lazy(() => import("@/pages/VolunteerPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const TaxExemptionPage = lazy(() => import("@/pages/TaxExemptionPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ServerError = lazy(() => import("@/pages/ServerError"));

// Pages — Auth
const LoginPage = lazy(() => import("@/features/auth/LoginPage"));

// Pages — Admin
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const UsersListPage = lazy(() => import("@/features/users/UsersListPage"));
const ProfilePage = lazy(() => import("@/features/users/ProfilePage"));
const NotificationsPage = lazy(() => import("@/features/notifications/NotificationsPage"));
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"));
const MediaPage = lazy(() => import("@/features/media/MediaPage"));
const MessagesPage = lazy(() => import("@/features/messages/MessagesPage"));
const CommunityPage = lazy(() => import("@/features/community/CommunityPage"));
const OrganizationPage = lazy(() => import("@/features/organization/OrganizationPage"));
const VolunteersListPage = lazy(() => import("@/features/volunteers/VolunteersListPage"));
const VolunteerDetailPage = lazy(() => import("@/features/volunteers/VolunteerDetailPage"));
const DonationsPage = lazy(() => import("@/features/donations/DonationsPage"));
const EventsPage = lazy(() => import("@/features/events/EventsPage"));
const AnadanPage = lazy(() => import("@/features/annadan/AnadanPage"));
const SamuhikVivahPage = lazy(() => import("@/features/samuhik_vivah/SamuhikVivahPage"));
const BloodDonationPage = lazy(() => import("@/features/blood_donation/BloodDonationPage"));
const MedicalCampsPage = lazy(() => import("@/features/medical_camps/MedicalCampsPage"));
const EducationPage = lazy(() => import("@/features/education/EducationPage"));
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage"));
const BeneficiariesPage = lazy(() => import("@/features/beneficiaries/BeneficiariesPage"));
const CmsPage = lazy(() => import("@/features/cms/CmsPage"));
const GalleryPage = lazy(() => import("@/features/gallery/GalleryPage"));
const AuditPage = lazy(() => import("@/features/audit/AuditPage"));
const LocalizationPage = lazy(() => import("@/features/localization/LocalizationPage"));
const FinancePage = lazy(() => import("@/features/finance/FinancePage"));
const AnalyticsPage = lazy(() => import("@/features/analytics/AnalyticsPage"));

export const routes: RouteObject[] = [
  // --- Public website ---
  {
    path: "/",
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "about", element: <AboutPage /> },
      { path: "programs", element: <ProgramsPage /> },
      { path: "programs/:program", element: <ProgramDetailPage /> },
      { path: "events", element: <PublicEventsPage /> },
      { path: "gallery", element: <PublicGalleryPage /> },
      { path: "contact", element: <ContactPage /> },
      { path: "volunteer", element: <VolunteerPage /> },
      { path: "ngo/:slug", element: <NgoPublicProfilePage /> },
      { path: "donate", element: <DonatePage /> },
      { path: "privacy", element: <PrivacyPage /> },
      { path: "terms", element: <TermsPage /> },
      { path: "80g", element: <TaxExemptionPage /> },
    ],
  },

  // --- Authentication ---
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      { index: true, element: <Navigate to="/auth/login" replace /> },
      { path: "login", element: <LoginPage /> },
    ],
  },

  // --- Admin dashboard (authenticated) ---
  {
    element: <PrivateRoute />,
    children: [
      {
        path: "/admin",
        element: <AdminLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "organization", element: <OrganizationPage /> },
          { path: "users", element: <UsersListPage /> },
          { path: "profile", element: <ProfilePage /> },
          { path: "notifications", element: <NotificationsPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "media", element: <MediaPage /> },
          { path: "messages", element: <MessagesPage /> },
          { path: "community", element: <CommunityPage /> },
          // Volunteers module
          { path: "volunteers", element: <VolunteersListPage /> },
          { path: "volunteers/:id", element: <VolunteerDetailPage /> },
          // Donations module
          { path: "donations", element: <DonationsPage /> },
          // Program modules
          { path: "events", element: <EventsPage /> },
          { path: "annadan", element: <AnadanPage /> },
          { path: "samuhik-vivah", element: <SamuhikVivahPage /> },
          { path: "blood-donation", element: <BloodDonationPage /> },
          { path: "medical-camps", element: <MedicalCampsPage /> },
          { path: "education", element: <EducationPage /> },
          { path: "reports", element: <ReportsPage /> },
          { path: "beneficiaries", element: <BeneficiariesPage /> },
          // Content modules
          { path: "cms", element: <CmsPage /> },
          { path: "gallery", element: <GalleryPage /> },
          // Platform tools
          { path: "audit", element: <AuditPage /> },
          { path: "localization", element: <LocalizationPage /> },
          { path: "finance", element: <FinancePage /> },
          { path: "analytics", element: <AnalyticsPage /> },
        ],
      },
    ],
  },

  // --- Error pages ---
  { path: "/500", element: <ServerError /> },
  { path: "*", element: <NotFound /> },
];
